<script lang="ts" setup>
import { onUnmounted, onMounted, watch } from 'vue'
import { GeoJSONSource, GeoJSONSourceSpecification } from 'mapbox-gl'
import { useMapBox } from './composables/useMapBox'
import { bbox } from '@turf/turf'
import { FeatureCollection } from 'geojson'

export interface Props {
	id: string
	spec: GeoJSONSourceSpecification
	initFitBounds?: boolean
}

const props = withDefaults(defineProps<Props>(), {
	initFitBounds: false,
})

const { map } = useMapBox()

onMounted(() => {
	if (!map?.value) throw new Error('mapbox map instance not found')
	map.value.addSource(props.id, props.spec)
})

function fitBounds(geojson: FeatureCollection) {
	if (!map?.value || !props.initFitBounds) return

	map.value.fitBounds(bbox(geojson) as any, {
		padding: 20,
		maxZoom: 13,
	})
}

function removeLayers() {
	if (!map?.value) return

	const style = map.value.style

	if (!style) return

	const layers = style._layers

	if (!layers) return

	const sourceLayers = Object.values(layers).filter((layer) => layer.source === props.id)

	if (sourceLayers) sourceLayers.forEach((layer) => map?.value?.removeLayer(layer.id))
}

function updateDataSource(data: any) {
	if (!map?.value) throw new Error('mapbox map instance not found')
	map.value.getSource<GeoJSONSource>(props.id)?.setData(data)
	fitBounds(data)
}

function removeSource() {
	if (!map?.value) return
	removeLayers()
	map.value.removeSource(props.id)
}

watch(
	() => props.spec.data,
	(value) => updateDataSource(value),
	{ immediate: true, deep: true },
)

onUnmounted(() => removeSource())
</script>

<template>
	<div style="display: none"></div>
</template>
